import { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { startTemplateFromExample, addNotification } from 'store/reducers/inputReducer';
import { selectActiveModelType, selectScenarioInfo } from 'store/reducers/selectors';
import { selectActiveExampleSlug } from 'store/reducers/selectors/settingsSelectors';
import { selectExampleBySlug } from 'store/reducers/uiReducer';
import { track, EVENTS } from 'utils/analytics';

const modelPages = require('store/data/model-pages.json');

// Long enough for the model page to mount and reset its notifications first.
export const NOTICE_DELAY_MS = 100;

// How many depot names the notice lists before "and N more".
const DEPOTS_NAMED = 3;

/**
 * The "template ready" notice, naming the depots that were kept.
 *
 * "Use as template" deliberately keeps depot rows (vehicles need somewhere to
 * start), and a Locations table that still has rows after "clear the sample
 * stops" was confusing without saying so.
 *
 * @param {Array<{name: string, isVehicleStart: boolean}>} depots - kept depot rows
 * @returns {string}
 */
export function templateReadyMessage(depots, strandedVehicles = 0) {
  const list = Array.isArray(depots) ? depots.filter((d) => d && d.name) : [];
  // A vehicle that started at a stop rather than a depot loses its starting
  // location when the stops go, and would otherwise only show up as a red row.
  const stranded = strandedVehicles > 0
    ? ` ${strandedVehicles} ${strandedVehicles === 1 ? 'vehicle started' : 'vehicles started'} at a stop we cleared, so ${strandedVehicles === 1 ? 'it needs' : 'they need'} a new starting location.`
    : '';
  if (list.length === 0) {
    return `Your template is ready.${stranded} Add your own stops to plan your routes.`;
  }
  const shown = list.slice(0, DEPOTS_NAMED).map((d) => d.name).join(', ');
  const more = list.length > DEPOTS_NAMED ? ` (and ${list.length - DEPOTS_NAMED} more)` : '';
  const noun = list.length === 1 ? 'depot' : 'depots';
  const role = list.every((d) => d.isVehicleStart) ? ' your vehicles start from' : '';
  return `Your template is ready. Kept ${list.length} ${noun}${role}: ${shown}${more}.${stranded} Add your own stops to plan your routes.`;
}

/**
 * Vehicles whose starting location was a cleared stop, so they now have none.
 * Compared with the state before the clone: a vehicle that never had a start was
 * already incomplete and is not something this action did.
 */
function strandedVehicleCount(before, after) {
  const had = new Set(
    ((before && before.data && before.data.vehicles) || [])
      .filter((row) => row && row['Starting Location'])
      .map((row) => row.UUID)
  );
  return ((after && after.data && after.data.vehicles) || [])
    .filter((row) => row && had.has(row.UUID) && !row['Starting Location']).length;
}

/** The depot rows left after startTemplateFromExample, with whether a vehicle starts at each. */
function keptDepots(present) {
  const data = (present && present.data) || {};
  const starts = new Set((data.vehicles || []).map((v) => v.startLocationId).filter(Boolean));
  return (data.locations || [])
    .filter((row) => row.isDepot)
    .map((row) => ({ name: row.locationName, isVehicleStart: starts.has(row.UUID) }));
}

/**
 * Turns the loaded worked example into the user's own template.
 *
 * The state change is one reducer action (startTemplateFromExample): keep the
 * example's depots, fleet, vehicle types and settings; drop its results, stops and
 * trips; detach it from the example; leave example mode. One action, so one Undo
 * restores the example whole.
 *
 * Also records the CTA, routes to the model page and raises the "Template ready"
 * notice. The notice waits until after navigation: the model page's dashboard
 * resets notifications when it mounts, and NotificationManager closes open
 * snackbars on every route change.
 */
export function cloneExampleAsTemplate({
  example,
  activeModelType = 'vehicle-routing',
  activeExampleSlug,
  scenarioInfo,
  dispatch,
  getState,
  history,
  ctaId = 'example_use_template',
  page,
}) {
  const modelType = example?.model_type || activeModelType || 'vehicle-routing';
  const slug = example?.slug || activeExampleSlug || 'unknown';
  const title = example?.title || example?.scenario_name || scenarioInfo?.scenarioName || 'Example';

  const stateBefore = getState ? getState().inputData.present : null;
  dispatch(startTemplateFromExample({ scenarioName: `${title} (My Fleet)` }));
  // Read straight after the clone, before navigating, so the names are the rows
  // that were actually kept.
  const clonedState = getState ? getState().inputData.present : null;
  const message = templateReadyMessage(
    clonedState ? keptDepots(clonedState) : [],
    clonedState ? strandedVehicleCount(stateBefore, clonedState) : 0
  );

  // Separate cta_ids per button, and `page`, to match the other CTA_CLICKED calls.
  // `page` is the surface the button was clicked on, in ctaPage()'s shape (the
  // route without its leading slash). The example page is the default because
  // that is where every caller but the gallery lives; the gallery passes its own
  // so its clicks are not reported as happening on the example page.
  track(EVENTS.CTA_CLICKED, {
    cta_id: ctaId,
    page: page || `examples/${slug}`,
    slug,
    modelType,
  });

  const targetRoute = modelPages[modelType] || '/models/vehicle-routing';
  if (history && history.push) {
    history.push(targetRoute);
  }

  setTimeout(() => {
    dispatch(
      addNotification({
        notificationMsg: message,
        notificationType: 'info',
      })
    );
  }, NOTICE_DELAY_MS);
}

/**
 * Hook providing a 1-click bridge from a worked example into custom modeling.
 *
 * The returned callback takes an optional cta_id, for components with more than
 * one button behind a single confirm dialog; anything else (a click event, say)
 * falls back to the hook's ctaId.
 */
export function useExampleAsTemplate(exampleProp, ctaId = 'example_use_template') {
  const dispatch = useDispatch();
  const store = useStore();
  const history = useHistory();
  const activeModelType = useSelector(selectActiveModelType);
  const activeExampleSlug = useSelector(selectActiveExampleSlug);
  const activeCatalogExample = useSelector(selectExampleBySlug(activeExampleSlug));
  const scenarioInfo = useSelector(selectScenarioInfo);

  const example = exampleProp || activeCatalogExample;

  return useCallback((ctaIdOverride) => {
    cloneExampleAsTemplate({
      example,
      activeModelType,
      activeExampleSlug,
      scenarioInfo,
      dispatch,
      getState: store.getState,
      history,
      ctaId: typeof ctaIdOverride === 'string' ? ctaIdOverride : ctaId,
    });
  }, [dispatch, store, history, example, activeModelType, activeExampleSlug, scenarioInfo, ctaId]);
}

export default useExampleAsTemplate;
